import { FormControl, FormLabel, TextField } from '@mui/material';

import { FormTextField } from '@/components/form/formTextField';
import {
  AutocompleteWithNew,
  SELECT_CREATE_NEW,
} from '@/components/shared/autocompleteWithNew';
import { BrowserGrid } from '@/components/shared/browserGrid';
import { useDAL } from '@/contexts/useDAL';

export const NetworkInterfaceConfig = ({
  accountId,
  resourceGroup,
  location,
  virtualNetworkName,
  setVirtualNetworkName,
  newVirtualNetworkName,
  setNewVirtualNetworkName,
  newSubnetName,
  setNewSubnetName,
  subnetName,
  setSubnetName,
}: {
  accountId: string;
  resourceGroup: string;
  location?: string;
  virtualNetworkName?: string;
  setVirtualNetworkName: (value: string) => void;
  newVirtualNetworkName?: string;
  setNewVirtualNetworkName: (value: string) => void;
  newSubnetName?: string;
  setNewSubnetName: (value: string) => void;
  subnetName?: string;
  setSubnetName: (value: string) => void;
}) => {
  return (
    <BrowserGrid>
      <VirtualNetworkSelect
        accountId={accountId}
        resourceGroup={resourceGroup}
        location={location}
        virtualNetworkName={virtualNetworkName}
        setVirtualNetworkName={(newName) => {
          setVirtualNetworkName(newName);
          setSubnetName(newName === SELECT_CREATE_NEW ? SELECT_CREATE_NEW : '');
        }}
      />
      {virtualNetworkName === SELECT_CREATE_NEW && (
        <FormTextField
          size='small'
          title='Virtual network name'
          value={newVirtualNetworkName || ''}
          onChange={(event) => setNewVirtualNetworkName(event.target.value)}
          placeholder='Enter virtual network name'
        />
      )}
      <SubnetSelect
        accountId={accountId}
        resourceGroup={resourceGroup}
        virtualNetworkName={virtualNetworkName}
        subnetName={subnetName}
        setSubnetName={setSubnetName}
      />
      {subnetName === SELECT_CREATE_NEW && (
        <FormTextField
          size='small'
          title='Subnet name'
          value={newSubnetName || ''}
          onChange={(event) => setNewSubnetName(event.target.value)}
          placeholder='Enter subnet name'
        />
      )}
    </BrowserGrid>
  );
};

const VirtualNetworkSelect = ({
  accountId,
  resourceGroup,
  location,
  virtualNetworkName,
  setVirtualNetworkName,
}: {
  accountId: string;
  resourceGroup: string;
  location?: string;
  virtualNetworkName?: string;
  setVirtualNetworkName: (value: string) => void;
}) => {
  const dal = useDAL();
  const { data: vnets } = dal.restore.azureRestoreConfig.useListVirtualNetworks(
    accountId,
    resourceGroup,
    location
  );
  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>Virtual network name</FormLabel>
      <AutocompleteWithNew
        value={virtualNetworkName || ''}
        onChange={(event, newValue) => setVirtualNetworkName(newValue || '')}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder='Select virtual network'
            label=''
            InputLabelProps={{ shrink: true }}
          />
        )}
        options={vnets?.virtualNetworks.map((vnet) => vnet.name) ?? []}
      />
    </FormControl>
  );
};

const SubnetSelect = ({
  accountId,
  resourceGroup,
  virtualNetworkName,
  subnetName,
  setSubnetName,
}: {
  accountId: string;
  resourceGroup: string;
  virtualNetworkName?: string;
  subnetName?: string;
  setSubnetName: (value: string) => void;
}) => {
  const dal = useDAL();
  const { data: subnets } = dal.restore.azureRestoreConfig.useListSubnets(
    accountId,
    resourceGroup,
    !virtualNetworkName || virtualNetworkName === SELECT_CREATE_NEW
      ? ''
      : virtualNetworkName
  );
  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>Subnet name</FormLabel>
      <AutocompleteWithNew
        value={subnetName || ''}
        onChange={(event, newValue) => setSubnetName(newValue || '')}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder='Select subnet'
            label=''
            InputLabelProps={{ shrink: true }}
          />
        )}
        options={subnets?.subnets.map((subnet) => subnet.name) ?? []}
      />
    </FormControl>
  );
};
